import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link as LinkIcon, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { todayStr } from '@/utils/dateUtils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldHint, Input, Label, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/misc';
import { ContentPreview } from '@/components/ContentPreview';
import { MediaEditor } from '@/components/MediaEditor';
import {
  CARD_TYPES,
  CARD_TYPE_LABELS,
  SOURCE_LANGUAGES,
  SOURCE_LANGUAGE_LABELS,
  sourceLanguageOf,
  type CardType,
  type ContentCard,
  type MediaItem,
  type SourceLanguage,
} from '@/types/content';

/** Everything the form edits. Shared by the Create and Edit pages (VAP-825). */
export interface ContentFormValues {
  cardType: CardType;
  /** Language of title/summary/body (VAP-827). Fixed after the card is created. */
  sourceLanguage: SourceLanguage;
  title: string;
  summary: string;
  body: string;
  publishDate: string;
  expirationDate: string;
  isPushFlagged: boolean;
  mediaUrls: MediaItem[];
  externalLinks: Array<{ url: string; label: string }>;
  /** Croatian translation override. Only on Edit of an English card that has a Croatian translation. */
  hr?: { title: string; summary: string; body: string };
}

export function emptyFormValues(): ContentFormValues {
  return {
    cardType: 'TIP',
    sourceLanguage: 'en',
    title: '',
    summary: '',
    body: '',
    publishDate: todayStr(),
    expirationDate: '',
    isPushFlagged: false,
    mediaUrls: [],
    externalLinks: [],
  };
}

export function formValuesFromCard(card: ContentCard): ContentFormValues {
  const sourceLanguage = sourceLanguageOf(card);
  const source = card.content[sourceLanguage];
  return {
    cardType: card.cardType,
    sourceLanguage,
    title: source?.title || '',
    summary: source?.summary || '',
    body: source?.body || '',
    publishDate: card.publishDate,
    expirationDate: card.expirationDate || '',
    isPushFlagged: card.isPushFlagged,
    mediaUrls: card.mediaUrls || [],
    externalLinks: (card.externalLinks || []).map(l => ({ url: l.url, label: l.label.en || '' })),
    // The Croatian box edits a translation; a Croatian card edits its Croatian text as the source
    hr: sourceLanguage !== 'hr' && card.content.hr
      ? { title: card.content.hr.title || '', summary: card.content.hr.summary || '', body: card.content.hr.body || '' }
      : undefined,
  };
}

const ALL_LANGS = ['en', 'hr', 'it', 'de', 'es', 'fr', 'sk', 'sv', 'sl', 'cs'] as const;

/** The card as the phone preview shows it while the admin types. */
export function previewCardFromValues(values: ContentFormValues, base?: ContentCard): ContentCard {
  const now = new Date().toISOString();
  return {
    cardId: base?.cardId ?? 'preview',
    status: base?.status ?? 'DRAFT',
    createdAt: base?.createdAt ?? now,
    updatedAt: now,
    createdBy: base?.createdBy ?? 'admin',
    metrics: base?.metrics ?? { fetchCount: 0, likeCount: 0, averageRating: 0, ratingCount: 0 },
    ...base,
    cardType: values.cardType,
    publishDate: values.publishDate,
    expirationDate: values.expirationDate || undefined,
    isPushFlagged: values.isPushFlagged,
    sourceLanguage: values.sourceLanguage,
    content: {
      ...(base?.content ?? {}),
      [values.sourceLanguage]: { title: values.title, summary: values.summary || values.title, body: values.body },
    },
    mediaUrls: values.mediaUrls,
    externalLinks: values.externalLinks.map(l => ({
      url: l.url,
      label: Object.fromEntries(ALL_LANGS.map(lang => [lang, l.label])) as Record<(typeof ALL_LANGS)[number], string>,
    })),
  } as ContentCard;
}

interface ContentFormProps {
  values: ContentFormValues;
  onChange: (values: ContentFormValues) => void;
  onSubmit: () => void;
  submitLabel: string;
  submitting: boolean;
  /** On Edit: the card id, so uploads go straight to images/<cardId>/. */
  cardId?: string;
  /** Extra boxes rendered after the English content (Create: translation switch; Edit: regenerate switch). */
  optionsSlot?: ReactNode;
  /** Extra boxes rendered after Media (Edit: legacy image row). */
  mediaSlot?: ReactNode;
  /** Base card for the preview (Edit). */
  previewBase?: ContentCard;
  /** Edit: the language cannot change after creation, because every translation came from it. */
  lockSourceLanguage?: boolean;
  /** Fields that are wrong, by name, shown under the field. */
  errors?: Partial<Record<'title' | 'body' | 'publishDate' | 'expirationDate', string>>;
}

export function ContentForm({
  values,
  onChange,
  onSubmit,
  submitLabel,
  submitting,
  cardId,
  optionsSlot,
  mediaSlot,
  previewBase,
  lockSourceLanguage = false,
  errors = {},
}: ContentFormProps) {
  const set = <K extends keyof ContentFormValues>(key: K, value: ContentFormValues[K]) => onChange({ ...values, [key]: value });

  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);

  const addLink = () => {
    const url = newLinkUrl.trim();
    const label = newLinkLabel.trim();
    if (!url || !label) return;
    if (!/^https?:\/\//i.test(url)) {
      setLinkError('The URL must start with http:// or https://');
      return;
    }
    set('externalLinks', [...values.externalLinks, { url, label }]);
    setNewLinkLabel('');
    setNewLinkUrl('');
    setLinkError(null);
  };

  const previewCard = useMemo(() => previewCardFromValues(values, previewBase), [values, previewBase]);

  // Keep the preview in view while the long form scrolls
  const [previewOpen, setPreviewOpen] = useState(true);
  useEffect(() => {
    const media = window.matchMedia('(min-width: 1280px)');
    setPreviewOpen(media.matches);
  }, []);

  const canSubmit = Boolean(values.title.trim() && values.body.trim() && values.publishDate) && !submitting;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
      className="grid grid-cols-1 gap-6 xl:grid-cols-5"
    >
      {/* Form column */}
      <div className="space-y-6 xl:col-span-3">
        <Card>
          <CardHeader>
            <CardTitle>Card type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {CARD_TYPES.filter(t => t !== 'DALMATIAN_WORD').map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => set('cardType', type)}
                  aria-pressed={values.cardType === type}
                  className={cn(
                    'rounded-md border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    values.cardType === type
                      ? 'border-primary bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
                      : 'border-border bg-card hover:bg-muted'
                  )}
                >
                  {CARD_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
            <div className="space-y-1.5">
              <CardTitle>Content ({SOURCE_LANGUAGE_LABELS[values.sourceLanguage]})</CardTitle>
              <CardDescription>
                {values.sourceLanguage === 'hr'
                  ? 'Write in Croatian. English and the 8 other languages are translated by Vapor AI from this text. The Croatian text shows no AI badge in the app.'
                  : 'Write in English. The 9 other languages are translated by Vapor AI from this text.'}
              </CardDescription>
            </div>
            <div role="radiogroup" aria-label="Language you write in" className="inline-flex rounded-md border border-border p-0.5">
              {SOURCE_LANGUAGES.map(lang => (
                <button
                  key={lang}
                  type="button"
                  role="radio"
                  aria-checked={values.sourceLanguage === lang}
                  disabled={lockSourceLanguage && values.sourceLanguage !== lang}
                  title={lockSourceLanguage ? 'The language is fixed after the card is created' : undefined}
                  onClick={() => {
                    if (lang === values.sourceLanguage) return;
                    const hasText = Boolean(values.title.trim() || values.summary.trim() || values.body.trim());
                    // Finding 12: the text stays, so make sure the admin means it; otherwise English
                    // text would be saved as Croatian and translated as if it were Croatian.
                    if (hasText && !window.confirm(`Keep the typed text and mark it as ${SOURCE_LANGUAGE_LABELS[lang]}?`)) return;
                    set('sourceLanguage', lang);
                  }}
                  className={cn(
                    'rounded px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                    values.sourceLanguage === lang ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                  )}
                >
                  {SOURCE_LANGUAGE_LABELS[lang]}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="cf-title">Title <span className="text-destructive">*</span></Label>
              <Input
                id="cf-title"
                className="mt-1.5"
                value={values.title}
                onChange={(e) => set('title', e.target.value)}
                required
                maxLength={100}
                placeholder="A short, concrete title"
                aria-invalid={Boolean(errors.title) || undefined}
              />
              <FieldHint className={cn('flex justify-between', errors.title && 'text-destructive')}>
                <span>{errors.title ?? ''}</span>
                <span className="tabular-nums">{values.title.length}/100</span>
              </FieldHint>
            </div>
            <div>
              <Label htmlFor="cf-summary">Summary</Label>
              <Input
                id="cf-summary"
                className="mt-1.5"
                value={values.summary}
                onChange={(e) => set('summary', e.target.value)}
                maxLength={200}
                placeholder="One sentence shown on the collapsed card (optional)"
              />
              <FieldHint className="flex justify-between">
                <span>Falls back to the title when empty.</span>
                <span className="tabular-nums">{values.summary.length}/200</span>
              </FieldHint>
            </div>
            <div>
              <Label htmlFor="cf-body">Body <span className="text-destructive">*</span></Label>
              <Textarea
                id="cf-body"
                className="mt-1.5 min-h-[180px]"
                value={values.body}
                onChange={(e) => set('body', e.target.value)}
                required
                placeholder="The full text shown when the card is expanded"
                aria-invalid={Boolean(errors.body) || undefined}
              />
              {errors.body && <FieldHint className="text-destructive">{errors.body}</FieldHint>}
            </div>
          </CardContent>
        </Card>

        {optionsSlot}

        {values.hr && values.sourceLanguage !== 'hr' && (
          <Card className="border-primary/30">
            <CardHeader>
              <CardTitle>Croatian translation</CardTitle>
              <CardDescription>Edit the Croatian text directly, or regenerate every translation from English.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="cf-hr-title">Title</Label>
                <Input id="cf-hr-title" className="mt-1.5" value={values.hr.title} maxLength={100}
                  onChange={(e) => set('hr', { ...values.hr!, title: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="cf-hr-summary">Summary</Label>
                <Input id="cf-hr-summary" className="mt-1.5" value={values.hr.summary} maxLength={200}
                  onChange={(e) => set('hr', { ...values.hr!, summary: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="cf-hr-body">Body</Label>
                <Textarea id="cf-hr-body" className="mt-1.5 min-h-[140px]" value={values.hr.body}
                  onChange={(e) => set('hr', { ...values.hr!, body: e.target.value })} />
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Media</CardTitle>
            <CardDescription>Images and videos shown on the card in the app.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <MediaEditor cardId={cardId} mediaUrls={values.mediaUrls} onChange={(m) => set('mediaUrls', m)} />
            {mediaSlot}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>External links</CardTitle>
            <CardDescription>Open in the phone browser from the expanded card.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {values.externalLinks.length > 0 && (
              <ul className="space-y-2">
                {values.externalLinks.map((link, index) => (
                  <li key={`${link.url}-${index}`} className="flex items-center gap-3 rounded-md bg-muted/60 px-3 py-2">
                    <LinkIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="text-sm font-medium">{link.label}</span>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate text-sm text-muted-foreground hover:text-primary">
                      {link.url}
                    </a>
                    <Button type="button" variant="ghost" size="icon-sm" aria-label={`Remove link ${link.label}`}
                      onClick={() => set('externalLinks', values.externalLinks.filter((_, i) => i !== index))}>
                      <X />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={newLinkLabel}
                onChange={(e) => setNewLinkLabel(e.target.value)}
                placeholder="Label"
                aria-label="Link label"
                className="sm:w-40"
              />
              <Input
                type="url"
                value={newLinkUrl}
                onChange={(e) => { setNewLinkUrl(e.target.value); setLinkError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
                placeholder="https://..."
                aria-label="Link URL"
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={addLink} disabled={!newLinkUrl.trim() || !newLinkLabel.trim()} aria-label="Add link">
                <Plus />
                Add
              </Button>
            </div>
            {linkError && <p className="text-sm text-destructive">{linkError}</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="cf-publish">Publish date <span className="text-destructive">*</span></Label>
                <Input id="cf-publish" type="date" className="mt-1.5" value={values.publishDate} required
                  onChange={(e) => set('publishDate', e.target.value)} aria-invalid={Boolean(errors.publishDate) || undefined} />
                {errors.publishDate && <FieldHint className="text-destructive">{errors.publishDate}</FieldHint>}
              </div>
              <div>
                <Label htmlFor="cf-expire">Expiration date</Label>
                <Input id="cf-expire" type="date" className="mt-1.5" value={values.expirationDate} min={values.publishDate}
                  onChange={(e) => set('expirationDate', e.target.value)} aria-invalid={Boolean(errors.expirationDate) || undefined} />
                <FieldHint className={cn(errors.expirationDate && 'text-destructive')}>
                  {errors.expirationDate ?? 'Optional. The card stops showing after this day.'}
                </FieldHint>
              </div>
            </div>
            <label className="flex items-center justify-between gap-4 rounded-md border border-border p-3">
              <span>
                <span className="block text-sm font-medium">Push flagged</span>
                <span className="block text-xs text-muted-foreground">Priority delivery: shown before other cards of the day.</span>
              </span>
              <Switch checked={values.isPushFlagged} onCheckedChange={(v) => set('isPushFlagged', v)} aria-label="Push flagged" />
            </label>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-3 xl:hidden">
          <Button type="button" variant="outline" onClick={() => setPreviewOpen(o => !o)}>
            {previewOpen ? 'Hide preview' : 'Show preview'}
          </Button>
          <Button type="submit" disabled={!canSubmit} loading={submitting}>{submitLabel}</Button>
        </div>
      </div>

      {/* Preview column */}
      <aside className={cn('xl:col-span-2', !previewOpen && 'hidden xl:block')}>
        <div className="space-y-4 xl:sticky xl:top-8">
          <ContentPreview card={previewCard} savedCard={previewBase} />
          <Button type="submit" className="hidden w-full xl:flex" size="lg" disabled={!canSubmit} loading={submitting}>
            {submitLabel}
          </Button>
        </div>
      </aside>
    </form>
  );
}
